import AnnouncementIcon from '@mui/icons-material/Announcement';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BorderColorIcon from '@mui/icons-material/BorderColor';
import CloseIcon from '@mui/icons-material/Close';
import DoDisturbOnIcon from '@mui/icons-material/DoDisturbOn';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import {
  Autocomplete,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItemText,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { useMutation,useQueries } from '@tanstack/react-query';
import { useEffect,useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import AttToken from '../../../../helpers/attToken';
import { trackEventMatomo } from '../../../../helpers/matomo';
import Aprovations from '../../../../service/Aprovations';
import Checklists from '../../../../service/Checklists';
import { ConjuntoType } from '../../../../service/Places';
import queryClient from '../../../../service/query';
import Users from '../../../../service/Users';
import { simpleFormType } from '../../../../types/checklist/types';

const usersService = new Users();
const formService = new Checklists();
const aprovationService = new Aprovations();

type respostaAprovadores = {
  id: string,
  nome: string,
  listaEquipes: string[]
}[] | null;

export type listaAprovadoresType = {
  [name: string]: string[]
};

export type prioridadeAprovacao = {
  [name: string]: {
    [form: string]: number;
  };
};

type props = {
  openModalAprove: boolean,
  setOpenModalAprove: Function,
  conjunto: ConjuntoType
};

export default function EditAproveModal({
  openModalAprove,
  setOpenModalAprove,
  conjunto
}: props
) {

  const { t } = useTranslation('translation');
  const [open, setOpen] = useState<{ bol: boolean, user: string }>({ bol: false, user: '' });
  const [selectedUsers, setSelectedUsers] = useState<listaAprovadoresType>(conjunto.listaAprovadores || {});
  const [selectedUsersV2, setSelectedUsersV2] = useState<prioridadeAprovacao>(conjunto.prioridadeAprovacao || {});

  const results = useQueries({
    queries: [{
      queryKey: ['GET_APROVADORES'],
      queryFn: async () => {
        const token = await AttToken();
        if (token) {
          const response: respostaAprovadores = await usersService.getAllAprovadores(token);
          return response;
        }
      },
      retry: 0,
      refetchOnWindowFocus: false,
      refetchOnMount: 'always',
    },
    {
      queryKey: ['GET_FORMS'],
      queryFn: async () => {
        const token = await AttToken();
        if (token) {
          const body = { ...token };
          const result: simpleFormType[] = await formService.getSimpleForm(body);
          return result;
        }
      },
      retry: 0,
      refetchOnWindowFocus: false,
      refetchOnMount: 'always',
    }
    ]
  });

  const isLoading = results?.some((result) => result.isLoading);
  const isFetching = results?.some((result) => result.isFetching);

  const { mutate, isPending } = useMutation({
    mutationKey: ['UPDATE_LISTA_APROVADORES'],
    mutationFn: async () => {
      const promise = toast.promise(async () => {
        const token = await AttToken();
        if (token) {
          const response = await aprovationService.updateListAprovers(token, conjunto.id, selectedUsers, selectedUsersV2);
          if (!response) throw new Error('erro ao salvar');
          return response;
        }
      }, {
        pending: t('Services.ServiceModal.savingChanges'),
        success: t('Services.ServiceModal.changesSaved'),
      }, { toastId: 'salvando' });
      return promise;
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: ['GET_PLACES'] });
      trackEventMatomo('Modal aprovadores', 'click', 'button', 'salva');
      setOpenModalAprove(false);
    },
  });

  const onLoading = isFetching || isLoading || isPending;

  useEffect(() => {
    conjunto.listaAprovadores && setSelectedUsers(conjunto.listaAprovadores);
  }, [conjunto.listaAprovadores]);

  function ListaAprove() {
    const removerChave = (nome: string) => {
      setSelectedUsers(prev => {
        const novaLista = { ...prev };
        delete novaLista[nome];
        return novaLista;
      });
      setSelectedUsersV2(prev => {
        const novaLista = { ...prev };
        delete novaLista[nome];
        return novaLista;
      });
    };

    return (
      <List component="nav">
        <Divider variant='middle' sx={{ marginTop: '10px', marginBottom: '10px' }} />
        <Typography variant='subtitle2' color='textDisabled' fontWeight={600} textAlign='center'>
          {t('aprovadores.aprovadoresSelecionados')}
        </Typography>
        <Divider variant='middle' sx={{ marginTop: '10px', marginBottom: '10px' }} />
        <Stack direction='column' sx={{ padding: 2 }}>
          {Object.keys(selectedUsers)?.map((user) =>
            <Stack direction='row' key={user} alignItems='center' spacing={2}>
              {selectedUsers[user]?.length > 0
                ? <Tooltip title={`${selectedUsers[user]?.length} ${t('aprovadores.qtdFormAssocia')}`}>
                  <TaskAltIcon color='success' />
                </Tooltip>
                : <Tooltip title={t('aprovadores.nenhumFormAssocia')}>
                  <RadioButtonUncheckedIcon color='error' />
                </Tooltip>}
              <ListItemText
                sx={{ wordBreak: 'break-word' }}
                primary={onLoading
                  ? <Skeleton variant='text' width={150} />
                  : usuario(user)} />
              <Tooltip title={t('aprovadores.editaFormAssocia')}>
                <span>
                  <IconButton
                    color='info'
                    disabled={onLoading}
                    onClick={() => setOpen({ bol: true, user: user })}
                  >
                    <BorderColorIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={t('aprovadores.removeAprova')}>
                <span>
                  <IconButton
                    color='error'
                    onClick={() => removerChave(user)}
                    disabled={onLoading}
                  >
                    <DoDisturbOnIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          )}
        </Stack>
      </List>
    );
  }

  const removerChave = (nome: string) => {
    setSelectedUsers((prev) => ({ ...prev, [open.user]: prev[open.user as keyof typeof prev].filter(ele => ele !== nome) }));
    setSelectedUsersV2((prev) => {
      const usuarioData = prev[open.user] ?? {};
      const { [nome]: _, ...restForms } = usuarioData;
      return {
        ...prev,
        [open.user]: restForms,
      };
    });
  };

  const usuario = (value: string): string | undefined => {
    const nome = results[0].data?.find((ele) => ele.id === value)?.nome;
    return nome;
  };

  const formulario = (value: string): string | undefined => {
    const nomeModelo = results[1].data?.find((elem) => elem.id === value)?.nome;
    return nomeModelo;
  };

  return (
    <Dialog
      fullWidth
      maxWidth='sm'
      open={openModalAprove}
      onClose={() => setOpenModalAprove(false)}
      scroll={'paper'}
    >
      <DialogTitle>{t('Services.ServiceModal.approverManagement')}</DialogTitle>
      {!open.bol
        ? <IconButton
          onClick={() => setOpenModalAprove(false)}
          sx={(theme) => ({
            position: 'absolute',
            right: 8,
            top: 8,
            color: theme.palette.grey[500],
          })}
        >
          <CloseIcon />
        </IconButton>
        : <Button
          sx={() => ({
            position: 'absolute',
            right: 8,
            top: 8,
          })}
          onClick={() => setOpen({ bol: false, user: '' })}
          startIcon={<ArrowBackIcon />}>
          {t('aprovadores.voltar')}
        </Button>
      }
      <DialogContent dividers={true}>
        {!open.bol && !open.user?.length ? <>
          <Autocomplete
            fullWidth
            onClick={() => trackEventMatomo('Modal aprovadores', 'click', 'select', 'seleciona valor')}
            value={undefined}
            disabled={!results[0].data?.length || (isLoading || isFetching)}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            getOptionLabel={option => option.nome}
            disableClearable
            disableCloseOnSelect
            renderOption={(props, option) => {
              const { key, ...optionProps } = props;
              return (
                <li key={key} {...optionProps}>
                  <Checkbox checked={Object.keys(selectedUsers).includes(option.id)} />
                  {option.nome}
                </li>
              );
            }}
            options={
              results[0].data
                ?.filter((user) => user?.listaEquipes)
                ?.sort((a, b) => { return a.nome.toLowerCase() > b.nome.toLowerCase() ? 1 : - 1; })
                ?.sort((a, b) => { return Object.keys(selectedUsers)?.includes(a.id) < Object.keys(selectedUsers)?.includes(b.id) ? 1 : - 1; })
                ?.map((ele) => {
                  return ({
                    nome: ele.nome,
                    id: ele.id,
                  });
                }) || []}
            onChange={(event, newValue: { nome: string, id: string }) => {
              event.preventDefault();
              if (Object.keys(selectedUsers)?.includes(newValue?.id)) {
                delete selectedUsers[newValue?.id];
                delete selectedUsersV2[newValue?.id];
                setSelectedUsers({ ...selectedUsers });
                setSelectedUsersV2({ ...selectedUsersV2 });
              } else {
                setSelectedUsers({ ...selectedUsers, [newValue.id]: [] });
                setSelectedUsersV2({ ...selectedUsersV2, [newValue.id]: {} });
              }
            }}
            renderInput={(params) => <TextField {...params} label={t('aprovadores.selectAprove')} />}
          />
          {Object.keys(selectedUsers).length > 0
            && <ListaAprove />}
        </>
          : <Stack direction='column' alignItems='flex-end'>
            <Autocomplete
              fullWidth
              onClick={() => trackEventMatomo('Modal aprovadores', 'click', 'select', 'seleciona valor')}
              disabled={!results[1].data?.length || (isLoading || isFetching)}
              value={undefined}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              getOptionLabel={option => option.nome}
              disableClearable
              disableCloseOnSelect
              noOptionsText={t('aprovadores.semEquipeAssocia')}
              options={
                results[1].data
                  ?.filter((el: simpleFormType) => {
                    const thisUser = results[0]?.data?.find((u) => u.id === open.user);
                    return thisUser ? thisUser?.listaEquipes?.some(equipeId => el?.equipes?.includes(equipeId)) : '';
                  })
                  ?.sort((a, b) => { return a.nome.toLowerCase() > b.nome.toLowerCase() ? 1 : - 1; })
                  ?.sort((a, b) => { return selectedUsers[open?.user]?.includes(a.id) < selectedUsers[open?.user]?.includes(b.id) ? 1 : - 1; })
                  ?.map((ele) => {
                    return ({
                      nome: ele.nome,
                      id: ele.id,
                    });
                  }) ??
                []}
              onChange={(_event, newValue: { nome: string, id: string }) => {
                const user = open.user;
                if (selectedUsers[user].includes(newValue?.id)) {
                  setSelectedUsers((prev) => ({ ...prev, [user]: prev[user as keyof typeof prev].filter(ele => ele !== newValue.id) }));
                  setSelectedUsersV2((prev) => {
                    const { [newValue.id]: _, ...restForms } = prev[user] ?? {}; return { ...prev, [user]: restForms, };
                  });
                } else {
                  setSelectedUsers((prev) => ({ ...prev, [open.user]: [...prev[open.user as keyof typeof prev], newValue.id] }));
                  setSelectedUsersV2((prev) => ({ ...prev, [open.user]: { ...prev[open.user as keyof typeof prev], [newValue.id]: 1, } }));
                }
              }}
              renderOption={(props, option) => {
                const { key, ...optionProps } = props;
                return (
                  <li key={key} {...optionProps}>
                    <Checkbox checked={selectedUsers[open.user]?.includes(option.id)} />
                    {option.nome}
                  </li>
                );
              }}
              renderInput={(params) => <TextField {...params} label={t('aprovadores.selecioneModelos')} />}
            />{selectedUsers[open.user].length > 0
              && <List
                sx={{ width: '100%' }}
                component="nav"
              >
                <Divider variant='middle' sx={{ marginTop: '10px', marginBottom: '10px' }} />
                <Typography variant='subtitle2' color='textDisabled' fontWeight={600} textAlign='center'>
                  {t('aprovadores.modelosAssocia')} {usuario(open.user)}
                </Typography>
                <Divider variant='middle' sx={{ marginTop: '10px', marginBottom: '10px' }} />
                <Stack direction='column' sx={{ overflow: 'auto', paddingTop: 2 }} spacing={2} >
                  {selectedUsers[open?.user]?.map((form) =>
                    <Stack direction='row' key={form} alignItems='center' spacing={2}>
                      <ListItemText sx={{ wordBreak: 'break-word' }} primary={formulario(form) ?? t('aprovadores.notFound')} />
                      <Tooltip title={t('aprovadores.prioridade')}>
                        <AnnouncementIcon />
                      </Tooltip>
                      <TextField
                        type='number'
                        label={t('aprovadores.ordem')}
                        sx={{ width: '100px' }}
                        value={selectedUsersV2?.[open?.user]?.[form] ?? 1}
                        onChange={(event) =>
                          setSelectedUsersV2((prev) => ({
                            ...prev,
                            [open.user]: {
                              ...prev[open.user as keyof typeof prev],
                              [form]: Number(event.target.value) < 1 ? 1 : Number(event.target.value)
                            }
                          }))
                        }
                        slotProps={{ htmlInput: { min: 1, style: { textAlign: 'center', width: '80px' } } }} />
                      <IconButton color='error' onClick={() => removerChave(form)} disabled={onLoading}>
                        <DoDisturbOnIcon />
                      </IconButton>
                    </Stack>
                  )}
                </Stack>
              </List>}
          </Stack>}
        {Object.values(selectedUsers)?.some((ele) => !ele.length)
          && <Typography color='textDisabled' variant='subtitle1'>
            {t('aprovadores.aprovadorPrecisaAssocia')}
          </Typography>}
      </DialogContent>
      <DialogActions>
        {!open.bol
          && <Button
            disabled={
              onLoading
              || Object.values(selectedUsers)?.some((ele) => !ele.length)}
            onClick={() => mutate()}
          >
            {t('save')}
          </Button>}
      </DialogActions>
    </Dialog>
  );
}